<script setup lang="ts">
import { ref, computed } from 'vue';
import Button from 'primevue/button';
import { useAuthStore } from '@/stores/authStore';
import { useMitLogo } from '@/common/composables/useMitLogo';
import { useTheme, type ThemeChoice } from '@/common/composables/useTheme';
import { useNotificationStore } from '@/stores/notificationStore';
import { CostObjectApprovalRecord, DormantKeyNotification } from '@/types/parley';
import router from '@/router';
import {
  approvalNotificationId,
  dormantKeyNotificationId,
} from '@/common/composables/useNotificationReadState';

const { choice: themeChoice, cycle: cycleTheme } = useTheme();
const { url: logoUrl, inverted: logoInverted } = useMitLogo();
const THEME_LABELS: Record<ThemeChoice, { icon: string; label: string }> = {
  system: { icon: 'pi pi-desktop', label: 'Theme: follows your system' },
  light: { icon: 'pi pi-sun', label: 'Theme: light' },
  dark: { icon: 'pi pi-moon', label: 'Theme: dark' },
};
const themeButton = computed(() => THEME_LABELS[themeChoice.value]);

const authStore = useAuthStore();
const notificationStore = useNotificationStore();

const notificationPopoverOpen = ref(false);

function toggleNotificationPopOver() {
  notificationPopoverOpen.value = !notificationPopoverOpen.value;
}

// Viewing marks the entry read, so it leaves the bell. The request itself
// stays pending until the approval page decides it.
const viewNotification = (notification: CostObjectApprovalRecord) => {
  notificationPopoverOpen.value = false;
  notificationStore.markNotificationsRead([approvalNotificationId(notification)]);
  // Only the request id travels in the URL. The approval page loads the
  // requester, account, and cost object details from the API.
  router.push({
    name: 'CostObjectApproval',
    params: {
      requestId: notification.accountId,
    },
  });
};

// Pure navigation, identical for both the 'warning' and 'paused' states —
// the actual pause/unpause stays a deliberate, separate click on the
// existing toggle on the account-detail page.
const viewDormantKeyNotification = (notification: DormantKeyNotification) => {
  notificationPopoverOpen.value = false;
  notificationStore.markNotificationsRead([dormantKeyNotificationId(notification)]);
  router.push({
    name: 'AccountDetail',
    params: {
      id: notification.accountId,
    },
    query: {
      highlightKey: notification.hashedApiKey,
    },
  });
};

const readAllNotifications = () => {
  notificationStore.markAllNotificationsRead();
  notificationPopoverOpen.value = false;
};

// Only unread entries reach the bell; a read one comes back only if the API
// reports a new occurrence (a key warned again, or paused after a warning).
const notifications = computed(() => notificationStore.unreadSupervisorApprovals);
const dormantKeyNotifications = computed(() => notificationStore.unreadDormantKeyNotifications);
const totalNotificationCount = computed(() => notificationStore.unreadNotificationCount);
</script>

<template>
  <header class="app-topbar">
    <div class="topbar-left">
      <router-link to="/" class="brand">
        <img
          :src="logoUrl"
          alt="MIT"
          class="brand-logo"
          :class="{ 'brand-logo--inverted': logoInverted }"
        />
        <span class="brand-name">Parley Platform</span>
      </router-link>
    </div>

    <div v-if="authStore.isAuthenticated" class="topbar-right">
      <div>
        <div class="relative inline-block">
          <span
            v-if="totalNotificationCount > 0"
            class="absolute -top-2 -right-2 z-10 flex items-center justify-center rounded-full h-6 w-6 text-xs font-semibold text-white bg-red-600"
            >{{ totalNotificationCount }}</span
          >
          <Button
            class="impersonate-btn"
            :class="{ 'pointer-events-none': !totalNotificationCount }"
            icon="pi pi-bell"
            :disabled="totalNotificationCount === 0"
            @click="toggleNotificationPopOver"
          />
        </div>
        <div
          v-if="notificationPopoverOpen"
          class="notification-popover"
          role="dialog"
          aria-label="Notifications"
        >
          <div class="popover-title border-b border-[var(--border-2)] mb-1">
            <span>Notifications</span>
            <Button
              label="Read all"
              icon="pi pi-check"
              size="small"
              severity="secondary"
              text
              aria-label="Mark all notifications as read"
              @click="readAllNotifications"
            />
          </div>

          <div class="max-h-72 overflow-auto">
            <div v-if="notifications.length > 0" class="notification-section">
              <p class="notification-section-title">Cost Object Approvals</p>
              <div
                v-for="(notification, index) in notifications"
                :key="notification.accountId ?? index"
                :class="[
                  'p-4 transition-colors duration-150 border-b border-[var(--border)] rounded-md',
                  index % 2 !== 0 ? 'bg-[var(--surface-2)]' : 'bg-[var(--surface)]',
                  'hover:bg-[var(--surface-2)]',
                ]"
              >
                <div class="flex items-center justify-between gap-4">
                  <div class="flex-1 min-w-0">
                    <p class="font-medium text-sm text-[var(--ink)]">Cost Object Approval</p>

                    <p class="text-sm text-[var(--ink-2)] mt-1">
                      Account:
                      <span class="font-medium">
                        {{ notification.accountName ?? notification.pendingPayload?.accountName }}
                      </span>
                    </p>

                    <p class="text-xs text-[var(--ink-3)] mt-1">
                      Requested by {{ notification.requesterEmail }}
                    </p>
                  </div>

                  <Button
                    label="View"
                    icon="pi pi-eye"
                    size="small"
                    severity="secondary"
                    outlined
                    @click="viewNotification(notification)"
                  />
                </div>
              </div>
            </div>

            <div v-if="dormantKeyNotifications.length > 0" class="notification-section">
              <p class="notification-section-title">Dormant Key Pauses</p>
              <div
                v-for="(notification, index) in dormantKeyNotifications"
                :key="`${notification.accountId}-${notification.hashedApiKey}`"
                :class="[
                  'p-4 transition-colors duration-150 border-b border-[var(--border)] rounded-md',
                  index % 2 !== 0 ? 'bg-[var(--surface-2)]' : 'bg-[var(--surface)]',
                  'hover:bg-[var(--surface-2)]',
                ]"
              >
                <div class="flex items-center justify-between gap-4">
                  <div class="flex-1 min-w-0">
                    <p class="font-medium text-sm text-[var(--ink)]">
                      {{
                        notification.state === 'paused'
                          ? 'API Key Auto-Paused'
                          : 'API Key Will Be Auto-Paused Soon'
                      }}
                    </p>

                    <p class="text-sm text-[var(--ink-2)] mt-1">
                      Account:
                      <span class="font-medium">{{ notification.accountName }}</span>
                    </p>

                    <p class="text-xs text-[var(--ink-3)] mt-1">
                      Key "{{ notification.label }}" ({{ notification.krbName }})
                    </p>
                  </div>

                  <Button
                    label="View"
                    icon="pi pi-eye"
                    size="small"
                    severity="secondary"
                    outlined
                    @click="viewDormantKeyNotification(notification)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="theme-toggle"
        :title="`${themeButton.label}. Click to change.`"
        :aria-label="`${themeButton.label}. Click to change.`"
        @click="cycleTheme"
      >
        <i :class="themeButton.icon" aria-hidden="true" />
      </button>

      <div class="user-chip">
        <div class="user-text">
          <div class="user-name">{{ authStore.displayName }}</div>
          <div v-if="authStore.roleLoaded" class="user-role">{{ authStore.roleLabel }}</div>
        </div>
        <img
          v-if="authStore.profilePictureUrl"
          :src="authStore.profilePictureUrl"
          :alt="authStore.displayName"
          class="user-avatar-img"
        />
        <div v-else class="user-avatar" aria-hidden="true">{{ authStore.userInitials }}</div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.app-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  z-index: 200;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  text-decoration: none;
  color: var(--ink);
  line-height: 1;
}

.brand-logo {
  height: 32px;
  width: auto;
  flex-shrink: 0;
  align-self: baseline;
}

/* Stand-in until the white logo file exists: the black logo, inverted. */
.brand-logo--inverted {
  filter: invert(1);
}

.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-2);
  border-radius: var(--radius-md);
  background: var(--surface);
  color: var(--ink-2);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.theme-toggle:hover {
  background: var(--surface-2);
  color: var(--ink);
}

.theme-toggle:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.brand-name {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1;
}

.impersonate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 8px;
  color: var(--ink-2);
  cursor: pointer;
  position: relative;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.impersonate-btn:hover {
  background: rgba(25, 102, 255, 0.08);
  border-color: rgba(25, 102, 255, 0.25);
  color: var(--blue);
}

.impersonate-btn :deep(.pi) {
  font-size: 14px;
}

.notification-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 100%;
  max-width: 650px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 14px 14px 12px;
  z-index: 250;
}

.popover-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 6px;
  padding-bottom: 4px;
}

.notification-section + .notification-section {
  margin-top: 10px;
}

.notification-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--ink-3);
  margin: 0 0 4px;
  padding: 0 4px;
}

.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.user-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
  min-width: 0;
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-role {
  font-size: 11px;
  color: var(--ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-avatar,
.user-avatar-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, var(--blue) 0%, #0a0a0a 100%);
  letter-spacing: 0.02em;
}

.user-avatar-img {
  object-fit: cover;
}

@media (max-width: 720px) {
  .app-topbar {
    padding: 0 16px;
  }

  .user-text {
    display: none;
  }
}
</style>
